import {globals, globalUser} from "./globals.js";
import {
    addFormerCompanyRelationship,
    getActiveCompetitorsForCompany,
    getCompanyLatestEvent,
    getCompanyMainRosterSuperstars,
    getCompanyRosterByType,
    getCompanyRosterCapacity,
    getCompanyStartingMonth,
    getHighestLevelStaffMemberByRole,
    getTutorialStateName,
    updateCompanyLogsForTalentNameChange
} from "./companies.js";
import {
    getCompanyRosters,
    getCompanyChampionships,
    getCompanyStaff,
    addCompanyCompanyLogs,
    getCompanyMessages, getCompanyCompanyLogs, getCompanyFeudItems, addCompanyNewsItems
} from "../models/company.js";
import {
    getCurrentGame,
    getRandomInt,
    getRandomChoice,
    getUniqueObjects,
    objectInArray,
    getRealDate,
    getArrayMaxByProperty,
    myround,
    getRosterMemberStreaks,
    getRosterMemberWonTournaments,
    findNameInString, getRosterMemberPronoun, getRandomSample, limitValueBetweenRange, checkAreArraysEqual
} from "./helpers.js";
import {getGameCurrentUserCompany, getGameActiveCompanies, getGameMainUserCompany} from "./games.js";
import {
    Superstars, TagTeams, Companies, Factions, CompanyLogs, Relationships, Feuds, Gimmicks, TalentRequests,
    db, Events
} from "../models/models.js";
import {
    getRosterSuperstars,
    getRosterTagTeams,
    addRosterTagTeams,
    addRosterSuperstars,
    getRosterBrands,
    addRosterFeuds
} from "../models/roster.js";
import {addTagTeam, getTagTeamFeuds, getTagTeamMatches, getTagTeamSuperstars} from "../models/tagTeam.js";
import {
    getSuperstarDisplayData,
    getRosterMemberSuperstars,
    getInjuryReturnTime,
    getRankFromPopularity,
    getSuperstarGenderFromPronoun,
    getSuperstarFreebirdTagChampionships,
    removeSuperstarFactionRelationships,
    removeSuperstarManagerRelationships,
    removeSuperstarTagTeams,
    changeSuperstarMoraleToHappy,
    getEmptySuperstarGraphic,
    getGimmickCombinationResult,
    getGimmickData,
    updateSuperstarMorale,
    checkSuperstarBrandAvailability,
    checkObjectBrandAvailability,
    getSuperstarChampionshipsFromCompanyChampionships,
    checkAvailability,
    checkSuperstarSegmentAvailability,
    getSuperstarActiveFactions, getSuperstarManagerUsedForMatchOrSegment
} from "./superstars.js";
import {getTagTeamDisplayData, getTagTeamPopularity, removeTagTeam} from "./tagTeams.js";
import {
    getSuperstarTagTeams,
    getSuperstarRoster,
    getSuperstarRelationships,
    getSuperstarMatches,
    getSuperstarChampionshipReigns,
    Superstar,
    getSuperstarChampionships,
    getSuperstarRandomEvents,
    getSuperstarTalentRequests,
    getSuperstarFeuds,
    removeSuperstarGimmicks,
    addSuperstarGimmicks,
    getSuperstarCurrentGimmick
} from "../models/superstar.js";
import {
    getUniqueChampionships,
    compareChampionships,
    getChampionshipHolder,
    getChampionshipBookingData
} from "./championships.js";
import {
    getChampionshipCompany,
    getChampionshipMatchContract,
    getChampionshipMatchContracts,
    getChampionshipSuperstar
} from "../models/championship.js";
import {getManagerData} from "./managers.js";
import {getMatchContractEvent} from "../models/matchContract.js";
import {getFactionData, getRosterActiveFactions} from "./factions.js";
import {CompanyLog} from "../models/companyLog.js";
import {getGameCompanies, getGameYearlyRankings} from "../models/game.js";
import {getChampionshipReignChampionship, getChampionshipReignMatches} from "../models/championshipReign.js";
import {getChampionshipDisplayName, objectEquals} from "../ui/ui-helpers.js";
import {addFeudSuperstars, Feud, getFeudParticipants} from "../models/feud.js";
import {getMatchParticipants} from "../models/match.js";
import {getMatchEventTitle, getSuperstarsBookedAtEventMatches} from "./matches.js";
import {getStreakEvent} from "../models/streak.js";
import {endFeud, getFeudData, getPreviousFeudHypeBoost, getRosterMemberFeuds} from "./feuds.js";
import {traitsData} from "../data/traits.js";
import {removeObjectBrand, setObjectBrand} from "./brands.js";
import {charactersData} from "../data/characters.js";
import {gimmickCombinationsData} from "../data/gimmickCombinations.js";
import {Gimmick} from "../models/gimmick.js";
import {getEventSegments} from "../models/event.js";
import {getSegmentParticipants} from "../models/segment.js";
import {addFeudItemFeuds} from "../models/feudItem.js";
import {getFactionFeuds, getFactionSuperstars} from "../models/faction.js";
import {generateNewsItem} from "./news.js";
import {checkTalentRequestAction} from "./talentRequests.js";
import {loadUser, saveUser} from "./user.js";
import {powDialog} from "../data/dialogTrees.js";
import {checkToGoToNextTutorialState} from "./tutorial.js";

async function getAvailableCallupsData() {  // get_available_training_roster
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const companyRosters = await getCompanyRosters(userCompany);

    // Get training and training available rosters
    const trainingRoster = await getCompanyRosterByType(userCompany, 'training', companyRosters);
    const trainingAvailableRoster = await getCompanyRosterByType(userCompany, 'trainingAvailable', companyRosters);

    // Transfer training superstar to available training roster if not already done
    if (!userCompany.trainingTransferRun) {
        const talentToTransfer = getRandomChoice(await getRosterSuperstars(trainingRoster));
        if (talentToTransfer) {
            await transferTalentToRoster(talentToTransfer, trainingAvailableRoster);
        }
        userCompany.trainingTransferRun = true;
        db.saveDatabase();
    }

    // Get simplified data for training available roster
    return getRosterDataSimplified(trainingAvailableRoster);
}

async function getRosterDataSimplified(roster, company = null, getInterestedRelationships = false) {  // Previously get_simplified_roster
    let data = [];

    // Get roster superstars, teams and superstars in teams
    const rosterSuperstars = await getRosterSuperstars(roster);
    const rosterTagTeams = await getRosterTagTeams(roster);
    let superstarsInTeams = (await Promise.all(
        rosterTagTeams.map(x => {
            return getTagTeamSuperstars(x)
        })
    )).flat();

    // Get dict of managed superstars and managers
    let managedSuperstarsDict = {};
    let managersDict = {};
    for (const s of rosterSuperstars) {
        if (s.managerId) {
            if (managedSuperstarsDict[s.managerId]) {
                managedSuperstarsDict[s.managerId].push(s)
            }
            else {
                managedSuperstarsDict[s.managerId] = [s]
            }
        }
    }
    for (const s of rosterSuperstars) {
        const managedSuperstars = managedSuperstarsDict[s.$loki] || [];
        for (const x of managedSuperstars) {
            managersDict[x.$loki] = s
        }
    }

    // Get superstars and teams data
    for (const superstar of rosterSuperstars) {
        if (superstarsInTeams.map(x => x.$loki).indexOf(superstar.$loki) >= 0) {
            continue
        }
        let managedSuperstars = managedSuperstarsDict[superstar.$loki] || [];
        let manager = managersDict[superstar.$loki] || null;
        let superstarData = await getSuperstarDisplayData({superstar: superstar, includeChampionships: false,
            rosterSuperstars: rosterSuperstars, managedSuperstars: managedSuperstars, manager: manager});
        superstarData["uid"] = superstar.$loki;
        superstarData["type"] = "superstar";
        if (getInterestedRelationships) {
            // Get interested relationship
            const interestedRelationship = (await getSuperstarRelationships(superstar))
                .find(x => x.interested && company && x.companyId === company.$loki);
            if (interestedRelationship) {
                superstarData['interested'] = true
            }
        }
        data.push(superstarData);
    }
    for (const tagTeam of rosterTagTeams) {
        let teamData = await getTagTeamDisplayData({tagTeam: tagTeam, rosterSuperstars: rosterSuperstars});
        teamData["uid"] = tagTeam.$loki;
        teamData["type"] = "tagTeam";
        if (getInterestedRelationships) {
            // Get interested relationship
            const interestedRelationships = [];
            const tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);
            for (const s of tagTeamSuperstars) {
                const interestedRelationship = (await getSuperstarRelationships(s))
                    .find(x => x.interested && company && x.companyId === company.$loki);
                if (interestedRelationship) {
                    interestedRelationships.push(interestedRelationship)
                }
            }
            if (interestedRelationships.length === tagTeamSuperstars.length) {
                teamData['interested'] = true
            }
        }
        data.push(teamData);
    }
    return data;
}

async function callupAvailableTrainingTalent(talentUid, talentType) {  // Previously callup_from_training
    // Performs callup from available training roster (when user is presented option to callup talent in specific months)

    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);

    // Get main roster
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');

    // Check if callup already run
    if (userCompany.trainingCallupRun) {
        data['error'] = 'callup-already-run';
        return data;
    }

    // Get talent
    const talent = await getTalentByIdAndType(parseInt(talentUid), talentType);

    // Check if talent found
    if (!talent) {
        data['error'] = 'talent-not-found';
        return data;
    }

    // Callup talent to main roster
    const overnessIncrease = await callupTalentToMainRoster(talent, mainRoster, userCompany, currentGame);

    // Increase morale for talent superstars
    const talentSuperstars = await getRosterMemberSuperstars(talent);
    await Promise.all(talentSuperstars.map(x => {
        updateSuperstarMorale(x, getRandomInt(20, 25));
    }));

    // Get data
    data['overnessIncrease'] = overnessIncrease;
    data['name'] = talent.name;
    data['pronoun'] = talent.possessivePronoun;

    // Change trainingCallupRun to true
    userCompany.trainingCallupRun = true;

    db.saveDatabase();

    return data;
}

async function getTalentByIdAndType(talentId, talentType) {
    let talent;
    if (talentType === 'superstar') {
        talent = await Superstars.get(talentId)
    }
    else if (talentType === 'tagTeam') {
        talent = await TagTeams.get(talentId)
    }
    return talent;
}

async function callupTalentToMainRoster(talent, mainRoster, company, game) {  // Previously callup_to_roster

    // Get random value for overness increase
    const overnessIncrease = getRandomInt(6, 8);

    // Transfer talent
    let transferredSuperstars = await transferTalentToRoster(talent, mainRoster);

    // Process transferred superstars
    for (const transferredSuperstar of transferredSuperstars) {
        // Reset active effect
        transferredSuperstar.activeEffect = null;

        // Increase overness
        transferredSuperstar.overness += overnessIncrease;
        transferredSuperstar.overness = limitValueBetweenRange(transferredSuperstar.overness, 0, 100);

        // Remove from factions and managers
        await removeSuperstarFactionRelationships(transferredSuperstar, game.monthCount, game.year);
        await removeSuperstarManagerRelationships(transferredSuperstar);
    }

    // Add company log
    const newCompanyLog = CompanyLog(game.monthCount, company.realYear);
    await CompanyLogs.insert(newCompanyLog);
    newCompanyLog.text = talent.name;
    newCompanyLog.type = 'callup';
    newCompanyLog.realYear = game.year;
    await addCompanyCompanyLogs(company, [newCompanyLog]);

    return overnessIncrease;
}

async function transferTalentToRoster(talent, roster, transferTeams = true) {  // Previously transfer_superstar
    // Transfers superstars or tag teams to roster. If superstar, finds tag teams and transfers them and their
    // superstars too.

    let transferredSuperstars = [];

    // Transfer superstar
    if (talent.objectName === 'superstar') {

        // Find superstar's tag teams if transferTeams is true
        let superstarTagTeams = (transferTeams) ? (await getSuperstarTagTeams(talent)).filter(x => !x.temporary) : [];

        // If tag teams present, transfer them
        if (superstarTagTeams.length > 0) {
            for (const tagTeam of superstarTagTeams) {
                const t = await transferTagTeamToRoster(tagTeam, roster);
                transferredSuperstars = transferredSuperstars.concat(t);
            }
        }
        // Otherwise transfer superstar
        else {
            await addRosterSuperstars(roster, [talent]);
            transferredSuperstars.push(talent);
        }

    }
    // Transfer tagTeam
    else if (talent.objectName === 'tagTeam') {
        const t = await transferTagTeamToRoster(talent, roster);
        transferredSuperstars = transferredSuperstars.concat(t);
    }

    return transferredSuperstars;
}

async function transferTagTeamToRoster(tagTeam, roster) {
    // Transfer tagTeam and its superstars
    await addRosterTagTeams(roster, [tagTeam]);
    const tagTeamSuperstars = await getRosterMemberSuperstars(tagTeam);
    await addRosterSuperstars(roster, tagTeamSuperstars);
    return tagTeamSuperstars;
}

async function getViewRosterData(companyUid) {  // Previously get_roster_all
    const currentGame = await getCurrentGame();

    // Get selected company
    let selectedCompany;
    if (companyUid) {
        selectedCompany = await Companies.get(companyUid)
    }
    else {
        selectedCompany = await getGameCurrentUserCompany(currentGame)
    }

    // Get main roster superstars
    const companyRosters = await getCompanyRosters(selectedCompany);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main', companyRosters);
    const mainRosterSuperstars = await getRosterSuperstars(mainRoster);
    const activeMainRosterSuperstars = mainRosterSuperstars.filter(x => !(x.retired && !x.fullTimeManager) && !x.dead);

    // Get data for superstars, tag teams and championships
    let data = await getViewRosterSuperstarsData(companyRosters, selectedCompany,
        mainRosterSuperstars, activeMainRosterSuperstars);
    data["tagTeams"] = await getViewRosterTagTeamsData(mainRoster);
    data["championships"] = await getViewRosterChampionshipsData(selectedCompany, mainRosterSuperstars, currentGame);

    // Get roster size and capacity
    data["rosterSize"] = activeMainRosterSuperstars.length;
    data["rosterCapacity"] = getCompanyRosterCapacity(selectedCompany, currentGame);

    // Get brand names
    const rosterBrands = await getRosterBrands(mainRoster);
    data["brandNames"] = rosterBrands.map(x => x.name);

    return data;
}

async function getViewRosterSuperstarsData(companyRosters, selectedCompany, mainRosterSuperstars,
                                           activeMainRosterSuperstars) {

    // Define data
    let data = {};
    data["superstars"] = [];
    data["managers"] = [];
    data["trainingSuperstars"] = [];
    data["retiredSuperstars"] = [];
    data["deadSuperstars"] = [];

    // Get doctor
    const staffMembers = await getCompanyStaff(selectedCompany);
    const doctor = getHighestLevelStaffMemberByRole(staffMembers, 'doctor');

    // Get training rosters
    const trainingRoster = await getCompanyRosterByType(selectedCompany, 'training', companyRosters);
    const trainingAvailableRoster = await getCompanyRosterByType(selectedCompany, 'trainingAvailable', companyRosters);
    const trainingFutureRoster = await getCompanyRosterByType(selectedCompany, 'trainingFuture', companyRosters);

    // Get training and training future superstars
    let trainingRostersSuperstars = (trainingRoster) ? await getRosterSuperstars(trainingRoster) : [];
    if (trainingAvailableRoster) {
        trainingRostersSuperstars = trainingRostersSuperstars.concat(await getRosterSuperstars(trainingAvailableRoster))
    }
    const trainingFutureRosterSuperstars = (trainingFutureRoster) ? await getRosterSuperstars(trainingFutureRoster) : [];

    // Get active training roster superstars
    let activeTrainingRosterSuperstars = trainingRostersSuperstars.filter(x => !(x.retired && !x.fullTimeManager) && !x.dead);

    // Get retired and dead superstars (main and training roster)
    const mainAndTrainingRosterSuperstars = mainRosterSuperstars.concat(trainingRostersSuperstars);
    let deadSuperstars = mainAndTrainingRosterSuperstars.filter(x => x.dead);
    let retiredSuperstars = mainAndTrainingRosterSuperstars.filter(x => (x.retired && !x.fullTimeManager) && !x.dead);

    // Get data for superstars, sorted by most popular
    for (const superstar of activeMainRosterSuperstars.concat(activeTrainingRosterSuperstars, trainingFutureRosterSuperstars,
        retiredSuperstars, deadSuperstars).sort((a, b) => b.popularity - a.popularity)) {
        let superstarData = {};
        superstarData["name"] = superstar.name;
        superstarData["uid"] = superstar.$loki;

        // Main roster
        if (activeMainRosterSuperstars.includes(superstar)) {
            superstarData["rank"] = getRankFromPopularity(superstar.popularity);
            superstarData["pronoun"] = superstar.pronoun;
            superstarData["alignment"] = superstar.alignment;
            superstarData["brandName"] = superstar.brandName;
            superstarData["faceOfTheCompany"] = superstar.faceOfTheCompany;
            superstarData["faceOfTheCompanyCounter"] = superstar.faceOfTheCompanyCounter;
            superstarData["suspensionMonths"] = superstar.suspensionMonths;
            superstarData["unhappy"] = superstar.unhappy;
            if (superstar.partTimer && !superstar.fullTimeManager) {
                superstarData["appearances"] = superstar.appearances
            }
            if (superstar.injured > 0) {
                superstarData["injured"] = superstar.injured;
                superstarData["injuryName"] = superstar.injuryName;
                superstarData["injuryTime"] = getInjuryReturnTime(superstar, doctor);
                superstarData["workableInjury"] = superstar.workableInjury;
            }
            if (superstar.fullTimeManager) {
                data["managers"].push(superstarData)
            }
            else {
                data["superstars"].push(superstarData)
            }
        }
        // Training roster
        else if (activeTrainingRosterSuperstars.includes(superstar)) {
            superstarData["future"] = false;
            data["trainingSuperstars"].push(superstarData)
        }
        // Training future roster
        else if (trainingFutureRosterSuperstars.includes(superstar)) {
            if (superstar.monthToAppearIn) {
                superstarData["name"] = "???";
                superstarData["future"] = true;
                superstarData["uid"] = null;
                superstarData["yearToAppearIn"] = Math.ceil(superstar.monthToAppearIn / 12);
                data["trainingSuperstars"].push(superstarData);
            }
        }
        // Retired (main and training roster)
        else if (retiredSuperstars.includes(superstar)) {
            superstarData["hallOfFamer"] = superstar.hallOfFamer;
            superstarData["inTrainingRoster"] = trainingRostersSuperstars.includes(superstar);
            data["retiredSuperstars"].push(superstarData)
        }
        // Dead (main and training roster)
        else if (deadSuperstars.includes(superstar)) {
            data["deadSuperstars"].push(superstarData)
        }
    }

    return data;
}

async function getViewRosterTagTeamsData(mainRoster) {
    let data = [];

    // Get roster tag teams
    const rosterTagTeams = await getRosterTagTeams(mainRoster);

    // Get data for tag teams
    for (const tagTeam of rosterTagTeams) {
        const tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);

        // Skip if no superstars or temporary team
        if (tagTeamSuperstars.length <= 0) {
            await removeTagTeam(tagTeam);
            db.saveDatabase();
            continue;
        }
        if (tagTeam.temporary) {
            continue;
        }

        let tagTeamData = {};
        tagTeamData["name"] = tagTeam.name;
        tagTeamData["uid"] = tagTeam.$loki;
        tagTeamData["trio"] = tagTeam.trio;
        tagTeamData["popularity"] = await getTagTeamPopularity(tagTeam, tagTeamSuperstars);
        tagTeamData["superstars"] = tagTeamSuperstars.map(x => x.name);

        data.push(tagTeamData);
    }

    // Sort tag teams by most popular
    data.sort((a, b) => (b.popularity - a.popularity));

    return data;
}

async function getViewRosterChampionshipsData(selectedCompany, mainRosterSuperstars, currentGame) {
    let data = [];

    // Get all championships, including competitors'
    const activeCompanies = await getGameActiveCompanies(currentGame);
    let allChampionships = (await Promise.all(activeCompanies.map(x => {return getCompanyChampionships(x)}))).flat();

    // Filter out tag titles without match contracts
    allChampionships = await filterTagChampionshipsWithoutMatchContracts(allChampionships);

    // Get unique championships
    const uniqueChampionships = getUniqueChampionships(allChampionships);

    // Get company's championships and championships with champion in company's roster
    const mainRosterSuperstarIds = mainRosterSuperstars.map(x => x.$loki);
    let foundChampionships = [];
    for await (const c of uniqueChampionships) {
        const championshipCompany = await getChampionshipCompany(c);
        const championshipSuperstar = await getChampionshipSuperstar(c);
        if (championshipCompany.$loki === selectedCompany.$loki ||
            (championshipSuperstar && mainRosterSuperstarIds.indexOf(championshipSuperstar.$loki) >= 0)) {
            foundChampionships.push(c)
        }
    }

    // Sort championships
    foundChampionships.sort(compareChampionships);

    // Get data for championships
    for (const championship of foundChampionships) {
        let championshipData = {};
        championshipData["name"] = championship.name;
        championshipData["brandName"] = championship.brandName;
        championshipData["tagTeam"] = championship.tagTeam;
        championshipData["trio"] = championship.trio;
        championshipData["prestige"] = championship.prestige;

        // Get champion data
        let champion = await getChampionshipHolder(championship, false);
        if (champion) {
            championshipData["championName"] = champion.name;
            championshipData["championUid"] = champion.$loki;
        }

        // Get event if match contract present
        if (championship.matchContract) {
            const championshipMatchContract = await getChampionshipMatchContract(championship);
            if (championshipMatchContract) {
                championshipData['event'] = (await getMatchContractEvent(championshipMatchContract))?.name;
                championshipData['matchContract'] = true;
            }
        }

        data.push(championshipData);
    }

    return data;
}

async function filterTagChampionshipsWithoutMatchContracts(championships) {
    const filteredChampionships = [];
    for (const championship of championships) {
        if ((championship.tagTeam || championship.trio) && championship.matchContract) {
            const championshipMatchContracts = await getChampionshipMatchContracts(championship);
            if (championshipMatchContracts.length > 0) {
                filteredChampionships.push(championship)
            }
        }
        else {
            filteredChampionships.push(championship)
        }
    }
    return filteredChampionships;
}

async function getViewRosterMemberData({uid, type, companyUid} = {}) {  // get_roster_member_details
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);

    // Get selected company
    let selectedCompany;
    if (companyUid) {
        selectedCompany = await Companies.get(companyUid)
    }
    else {
        selectedCompany = userCompany
    }

    // Get staff
    const companyStaffMembers = await getCompanyStaff(userCompany);
    const scout = getHighestLevelStaffMemberByRole(companyStaffMembers, 'scout');
    const doctor = getHighestLevelStaffMemberByRole(companyStaffMembers, 'doctor');

    // Get roster member data
    let talent;
    let trainingTalent = false;
    const mainRosterSuperstars = await getCompanyMainRosterSuperstars(selectedCompany);
    if (type === 'superstar' || type === 'training-superstar') {
        talent = await Superstars.get(uid);
        data = await getSuperstarDisplayData({superstar: talent, rosterSuperstars: mainRosterSuperstars,
            getTeamsAndFactions: true, getTournamentsWon: true, doctor: doctor});

        // Check if superstar in available training roster
        const talentRoster = await getSuperstarRoster(talent);
        if (talentRoster.type === 'trainingAvailable') {
            data["available"] = true;
        }
        if (['training', 'trainingAvailable'].indexOf(talentRoster.type) >= 0) {
            trainingTalent = true
        }
    }
    else if (type === 'tagTeam') {
        talent = await TagTeams.get(uid);
        data = await getTagTeamDisplayData({tagTeam: talent, rosterSuperstars: mainRosterSuperstars,
            getTournamentsWon: true});
    }
    else if (type === 'faction') {
        talent = await Factions.get(uid);
        data = await getFactionData(talent, (await getCompanyChampionships(selectedCompany)));
    }

    // Get other data to return
    data['competitorTalent'] = !!companyUid;
    data['type'] = type;
    if (trainingTalent) {
        data['type'] = 'training-superstar'
    }
    data["scoutLevel"] = scout ? scout.level : 1;

    // Get talent stats for main roster superstars if JoW Paid or tutorial
    const tutorialStatsState = userCompany.storyName === 'tutorial' &&
        (await getTutorialStateName(userCompany)) === 'view_superstar_details';
    if ((globalUser.paid || tutorialStatsState) && ['superstar', 'tagTeam'].indexOf(type) >= 0 &&
        (!data['competitorTalent'] || data['scoutLevel'] >= 3)) {  // Get for competitor talent if level 3 scout
        const startingEvents = Events.chain().limit(13).data();
        const firstEvent = startingEvents[startingEvents.length - 1];
        const startingMonth = await getCompanyStartingMonth(startingEvents);
        const allCompanies = await getGameCompanies(currentGame);
        const companyLogs = (await Promise.all(allCompanies.map(x => getCompanyCompanyLogs(x)))).flat();
        const yearlyRankings = type === 'superstar' ? await getGameYearlyRankings(currentGame) : [];
        data['stats'] = await getTalentStats(talent, startingMonth, firstEvent.year, companyLogs, yearlyRankings);
    }

    // Check tutorial action
    if (userCompany.storyName === 'tutorial') {
        data['nextTutorialState'] = await checkToGoToNextTutorialState(userCompany, ['view_superstar_details',
            'view_indies']);
        db.saveDatabase();
    }

    return data;
}

async function getTalentStats(talent, startingMonth, startingYear, companyLogs, yearlyRankings) {  // get_talent_stats
    let data = {};

    // Get talent matches and reigns
    const talentNames = [talent.name];
    let talentMatches;
    let talentReigns = [];
    if (talent.objectName === 'superstar') {
        talentMatches = await getSuperstarMatches(talent);
        for (const tagTeam of (await getSuperstarTagTeams(talent))) {
            talentMatches.push(...await getTagTeamMatches(tagTeam));
            talentNames.push(tagTeam.name)
        }
        const superstarReigns = await getSuperstarChampionshipReigns(talent);
        for (const superstarReign of superstarReigns) {
            const c = await getChampionshipReignChampionship(superstarReign);
            if (c) {
                talentReigns.push(superstarReign)
            }
        }
    }
    else {
        talentMatches = await getTagTeamMatches(talent);
        const tagTeamSuperstars = await getTagTeamSuperstars(talent);
        const superstarReigns = await getSuperstarChampionshipReigns(tagTeamSuperstars[0]);
        const superstarTagReigns = [];
        for (const superstarReign of superstarReigns) {
            const c = await getChampionshipReignChampionship(superstarReign);
            if (c && (c.tagTeam || c.trio)) {
                superstarTagReigns.push(superstarReign)
            }
        }
        for (const superstarTagReign of superstarTagReigns) {
            // Check if specific number of members of team have this championship reign
            const c = await getChampionshipReignChampionship(superstarTagReign);
            const numberOfTitles = (c.tagTeam) ? 2 : 3;
            const sameReignMembers = [];
            for (const s of tagTeamSuperstars) {
                const r = await getSuperstarChampionshipReigns(s);
                if ((await checkIfReignPresentInReigns(superstarTagReign, r))) {
                    sameReignMembers.push(s)
                }
            }
            if (sameReignMembers.length === numberOfTitles) {
                talentReigns.push(superstarTagReign)
            }
        }
    }

    // Filter out matches not held
    talentMatches = talentMatches.filter(x => x.winner !== null);

    // Wins/losses
    data['wins'] = talent.wins;
    data['losses'] = talent.losses;
    const totalMatches = talent.wins + talent.losses;
    data['winPercentage'] = null;
    if (totalMatches > 0) {
        data['winPercentage'] = Math.round(talent.wins / totalMatches * 100)
    }

    // Winning/losing streaks
    data['winningStreak'] = talent.winsCounter;
    data['losingStreak'] = talent.lossesCounter;

    // Current feuds
    data['currentFeuds'] = [];
    const talentFeuds = (await getRosterMemberFeuds(talent)).reverse();
    const feuds = [...talentFeuds].filter(x => x.started && !x.ended);
    for (const feud of feuds) {
        const otherParticipants = (await getFeudParticipants(feud)).filter(x => x.$loki !== talent.$loki);
        data['currentFeuds'].push({'opponents': otherParticipants.map(x => x.name), 'hype': feud.hype,
            'name': feud.name});
    }

    // Biggest rival
    const pastFeuds = [...talentFeuds].filter(x => x.started && x.ended);
    if (pastFeuds.length > 0) {
        const topFeud = getArrayMaxByProperty(pastFeuds, 'hype');
        const topFeudParticipants = await getFeudParticipants(topFeud);
        const biggestRival = topFeudParticipants.find(x => x.$loki !== talent.$loki);
        if (biggestRival) {
            data['biggestRival'] = biggestRival.name
        }
    }

    // Past 5 matches and record
    data['recentMatches'] = [];
    const talentMatchesEvents = Events.find({'$loki': {'$in': talentMatches.map(x => x.eventId)}});
    const recentMatches = [...talentMatches].sort((a, b) => {
        let aEvent = talentMatchesEvents.find(x => x.$loki === a.eventId);
        let bEvent = talentMatchesEvents.find(x => x.$loki === b.eventId);
        // .year sorting fixes imported games bug where competitor eventIds were higher than company
        return bEvent.year - aEvent.year || bEvent.month - aEvent.month ||
            Number(aEvent.name === 'Weekly Show') - Number(bEvent.name === 'Weekly Show') ||
            b.eventId - a.eventId || a.order - b.order;
    }).slice(0, 5);
    for (const match of recentMatches) {
        const otherParticipants = (await getMatchParticipants(match)).filter(x => talentNames.indexOf(x.name) < 0);
        data['recentMatches'].push({
            'opponents': otherParticipants.map(x => x.name),
            'event': await getMatchEventTitle(match), 'win': talentNames.indexOf(match.winner) >= 0,
            'rating': match.rating
        });
    }

    if (talentMatches.length > 0) {
        // Greatest match
        const sortedMatches = [...talentMatches].sort((a, b) => b.rating - a.rating || b.eventId - a.eventId);
        const greatestMatch = sortedMatches[0];
        const otherParticipants = (await getMatchParticipants(greatestMatch)).filter(x => talentNames.indexOf(x.name) < 0);
        data['greatestMatch'] = {
            'opponents': otherParticipants.map(x => x.name),
            'event': await getMatchEventTitle(greatestMatch), 'win': talentNames.indexOf(greatestMatch.winner) >= 0,
            'rating': greatestMatch.rating
        };

        // Average match rating
        data['averageMatchRating'] = myround(talentMatches.filter(x => x.rating).map(y => y.rating)
            .reduce((partialSum, a) => partialSum + a, 0) / talentMatches.length, 0.01).toFixed(2);
    }

    // Streaks
    data['eventStreaks'] = [];
    for (const streak of (await getRosterMemberStreaks(talent))) {
        if (streak.length >= 2 && !streak.ended) {
            data['eventStreaks'].push({'event': (await getStreakEvent(streak))?.name, 'length': streak.length});
        }
    }

    // Awards and annual tournaments won
    data['awardsWon'] = [];
    for (const tournamentWon of (await getRosterMemberWonTournaments(talent))) {
        if (tournamentWon.annualTournament) {
            data['awardsWon'].push({'name': tournamentWon.name, 'award': 3})
        }
    }
    for (const companyLog of companyLogs.reverse()) {
        const isTGILog = companyLog.text.split(' ').slice(0, 3).join(' ') === 'The Grand Invitational';
        if (companyLog.type === 'award' && findNameInString(talent.name, companyLog.text) && !isTGILog) {
            data['awardsWon'].push({'name': companyLog.text, 'award': companyLog.value})
        }
    }

    // Yearly rankings history if in top 10
    data['yearlyRankingsHistory'] = [];
    for (const yearlyRanking of yearlyRankings) {
        const top10Ranking = yearlyRanking.data?.slice(0, 10).find(x => x.name === talent.name);
        if (top10Ranking) {
            data['yearlyRankingsHistory'].push({'rank': yearlyRanking.data.indexOf(top10Ranking) + 1,
                'year': yearlyRanking.year})
        }
    }
    data['yearlyRankingsHistory'].sort((a, b) => b.year - a.year);

    // Championship reigns in chronological order
    data['championshipReigns'] = [];
    talentReigns.sort((a, b) => (b.endMonth === null) - (a.endMonth === null) || (b.startMonth + (b.startYear * 12)) -
        (a.startMonth + (a.startYear * 12)));
    for (const reign of talentReigns) {
        const startDate = getRealDate(reign.startMonth, reign.startYear, startingMonth, startingYear);
        let endDate = null;
        if (reign.endMonth !== null) {
            endDate = getRealDate(reign.endMonth, reign.endYear, startingMonth, startingYear)
        }

        // Get number of defenses
        let numberOfDefenses;
        let matches = (await getChampionshipReignMatches(reign)).reverse();
        if (matches.length > 0 && matches[matches.length - 1].championshipsWon) {
            numberOfDefenses = matches.length - 1  // Lost in last defense
        }
        else {
            numberOfDefenses = matches.length
        }
        if (numberOfDefenses < 0) {
            numberOfDefenses = 0
        }

        const reignChampionship = await getChampionshipReignChampionship(reign);
        data['championshipReigns'].push({'defenses': numberOfDefenses, 'startDate': startDate, 'endDate': endDate,
            'name': getChampionshipDisplayName(reignChampionship)})
    }

    return data;
}

async function checkIfReignPresentInReigns(checkReign, reigns) {
    const checkC = await getChampionshipReignChampionship(checkReign);
    const checkM = await getChampionshipReignMatches(checkReign);
    for (const r of reigns) {
        const c = await getChampionshipReignChampionship(r);
        const m = await getChampionshipReignMatches(r);
        if (c && checkC && c.name === checkC.name && m.every(x => objectInArray(x, checkM))) {
            return true;
        }
    }
    return false;
}

async function getViewIndiesData() {  // get_available_indie_roster
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainCompany = await getGameMainUserCompany(currentGame);

    // Get indie and indie available rosters
    const indieRoster = await getCompanyRosterByType(mainCompany, 'indie');
    const indieAvailableRoster = await getCompanyRosterByType(mainCompany, 'indieAvailable');

    // Get scout level
    const scout = getHighestLevelStaffMemberByRole((await getCompanyStaff(userCompany)), 'scout');
    data["scoutLevel"] = scout ? scout.level : 1;

    // Get data for indie rosters
    data["available"] = await getRosterDataSimplified(indieAvailableRoster, userCompany, true);
    data["unavailable"] = await getRosterDataSimplified(indieRoster);

    return data;
}

async function getViewTeamsData(companyUid) {  // get_company_teams
    let data = [];
    const currentGame = await getCurrentGame();

    // Get selected company
    let selectedCompany;
    if (companyUid) {
        selectedCompany = await Companies.get(companyUid)
    }
    else {
        selectedCompany = await getGameCurrentUserCompany(currentGame)
    }

    // Get main roster's tag teams
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    let mainRosterTagTeams = await getRosterActiveTagTeams(mainRoster);

    // Sort by latest first
    mainRosterTagTeams.sort((a, b) => b.$loki - a.$loki);

    // Get data for tag teams
    for (const tagTeam of mainRosterTagTeams) {
        const tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);

        // Skip if no superstars
        if (tagTeamSuperstars.length <= 0) {
            await removeTagTeam(tagTeam);
            db.saveDatabase();
            continue;
        }

        let tagTeamData = {};
        tagTeamData["name"] = tagTeam.name;
        tagTeamData["uid"] = tagTeam.$loki;
        tagTeamData["superstars"] = tagTeamSuperstars.map(x => x.name);
        tagTeamData["trio"] = tagTeam.trio;

        data.push(tagTeamData);
    }

    return data;
}

async function getViewManagersData(companyUid) {  // get_company_managers_view
    const currentGame = await getCurrentGame();

    // Get selected company
    let selectedCompany;
    if (companyUid) {
        selectedCompany = await Companies.get(companyUid)
    }
    else {
        selectedCompany = await getGameCurrentUserCompany(currentGame)
    }

    // Get main roster's superstars
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    let mainRosterSuperstars = await getRosterSuperstars(mainRoster);

    // Get managers (full time managers and superstars managing other superstars)
    const managerIds = mainRosterSuperstars.filter(x => x.managerId).map(x => x.managerId);
    const managers = mainRosterSuperstars.filter(x => x.fullTimeManager || managerIds.indexOf(x.$loki) >= 0);

    // Get managers data
    let data = await Promise.all(managers.map(x => getManagerData(x, mainRosterSuperstars)));
    data.sort((a, b) => b.fullTimeManager - a.fullTimeManager || b.charisma - a.charisma);

    return data;
}

async function getRosterActiveTagTeams(roster) {
    return (await getRosterTagTeams(roster)).filter(x => !x.temporary);
}

async function getAddTeamData() {  // get_superstars
    const currentGame = await getCurrentGame();
    let selectedCompany = await getGameCurrentUserCompany(currentGame);
    const superstars = (await getCompanyMainRosterSuperstars(selectedCompany)).filter(x => !x.fullTimeManager)
        .map(y => y.name);
    superstars.sort();
    return superstars;
}

async function addTeam(name, firstSuperstarName, secondSuperstarName, thirdSuperstarName, teamType) {  // add_tag_team
    let data = {};
    const currentGame = await getCurrentGame();
    let selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const rosterTagTeams = await getRosterTagTeams(mainRoster);
    const rosterFactions = await getRosterActiveFactions(mainRoster);
    const cost = 20;
    data['tagTeamType'] = teamType === 'trio' ? 'Trio' : 'Tag Team';

    // Check if enough points
    if (selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Get superstars
    let firstSuperstar = rosterSuperstars.find(x => x.name === firstSuperstarName);
    let secondSuperstar = rosterSuperstars.find(x => x.name === secondSuperstarName);
    let thirdSuperstar = null;
    if (teamType === 'trio') {
         thirdSuperstar = rosterSuperstars.find(x => x.name === thirdSuperstarName);
    }

    // Check if superstars found
    const tagTeamSuperstars = [firstSuperstar, secondSuperstar];
    if (teamType === 'trio') {
        tagTeamSuperstars.push(thirdSuperstar)
    }
    if (tagTeamSuperstars.some(x => !x)) {
        data['error'] = 'not-found';
        return data;
    }

    // Check if team already exists
    if (rosterTagTeams.find(x => x.name === name)) {
        data['error'] = 'already-present';
        return data;
    }

    // Check if faction already exists
    if (rosterFactions.find(x => x.name === name)) {
        data['error'] = 'faction-already-present';
        return data;
    }

    // Check if superstars are present more than once
    if (getUniqueObjects(tagTeamSuperstars).length !== tagTeamSuperstars.length) {
        data['error'] = 'duplicate-superstars';
        return data;
    }

    // Check if superstars are already in a tag team
    for (const s of tagTeamSuperstars) {
        let superstarTagTeams;
        if (teamType === 'trio') {
            superstarTagTeams = (await getSuperstarTagTeams(s)).filter(x => !x.temporary && x.trio)
        }
        else {
            superstarTagTeams = (await getSuperstarTagTeams(s)).filter(x => !x.temporary && !x.trio)
        }
        if (superstarTagTeams.length > 0) {
            data['error'] = 'already-in-tag-team';
            data['superstarName'] = s.name;
            return data;
        }
    }

    // Create tag team and add to roster
    const newTagTeam = await addTagTeam(name, tagTeamSuperstars, teamType === 'trio');
    await addRosterTagTeams(mainRoster, [newTagTeam]);

    // Add company log
    const newCompanyLog = CompanyLog(currentGame.monthCount, selectedCompany.realYear);
    await CompanyLogs.insert(newCompanyLog);
    newCompanyLog.text = newTagTeam.name;
    newCompanyLog.type = 'new-tag-team';
    newCompanyLog.realYear = currentGame.year;
    await addCompanyCompanyLogs(selectedCompany, [newCompanyLog]);

    // Remove points
    selectedCompany.points -= cost;

    // Check talent request
    for (const s of tagTeamSuperstars) {
        for (const otherMember of tagTeamSuperstars.filter(x => x.$loki !== s.$loki)) {
            let requestData = {'talentId': otherMember.$loki};
            await checkTalentRequestAction({company: selectedCompany, requestType: 'team_with_x', talent: s,
                data: requestData});
        }
    }

    // Check tutorial action
    if (selectedCompany.storyName === 'tutorial') {
        data['nextTutorialState'] = await checkToGoToNextTutorialState(selectedCompany, ['add_team']);
    }

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;
    data['tagTeamName'] = newTagTeam.name;
    const unreadMessages = (await getCompanyMessages(selectedCompany)).filter(x => !x.read);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

async function getRosterMemberNameData({uid, type} = {}) {  // get_roster_member_name
    let data = {};

    // Get member
    let rosterMember;
    if (type === 'superstar' || type === 'training-superstar') {
        rosterMember = await Superstars.get(uid)
    }
    else if (type === 'tagTeam') {
        rosterMember = await TagTeams.get(uid)
    }
    else if (type === 'faction') {
        rosterMember = await Factions.get(uid)
    }

    data['name'] = rosterMember.name;

    return data;
}

async function getRosterMemberPronounData({uid} = {}) {  // get_roster_member_pronoun
    let data = {};
    let rosterMember = await Superstars.get(uid);
    data['name'] = rosterMember.name;
    data['pronoun'] = rosterMember.pronoun;
    return data;
}

async function getRosterMemberGimmickData({uid, getCharacters = true} = {}) {  // get_superstar_gimmick_information
    let data = {};
    let rosterMember = await Superstars.get(uid);
    let gimmick = await getSuperstarCurrentGimmick(rosterMember);

    data['name'] = rosterMember.name;
    data['graphic'] = rosterMember.graphic;
    data['alignment'] = rosterMember.alignment;
    if (gimmick) {
        data['gimmick'] = gimmick.name;
        data['nextGimmickBoost'] = gimmick.nextGimmickBoost;
    }

    // Get characters
    if (getCharacters) {
        const currentGame = await getCurrentGame();
        const selectedCompany = await getGameCurrentUserCompany(currentGame);
        const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
        const user = loadUser();
        const characters = [...charactersData];
        const usedCharacters = await getUsedCharacters(mainRoster, rosterMember);
        let unlockedCharacters = globalUser.paid ? user.unlockedCharacters : [];
        data['characters'] = characters.map(x => getCharacterData(x, user, usedCharacters, unlockedCharacters));
    }

    return data;
}

async function getUsedCharacters(roster, superstar = null) {  // get_used_characters
    const standardCharactersNames = [...charactersData].filter(x => x.standard).map(y => y.name);
    let rosterCharacters = [];
    for (const s of (await getRosterSuperstars(roster))) {
        // Check gimmicks of all other superstars
        if (!superstar || s.$loki !== superstar.$loki) {
            const gimmick = await getSuperstarCurrentGimmick(s);
            if (gimmick && gimmick.character) {
                if (standardCharactersNames.includes(gimmick.character)) {
                    rosterCharacters.push(gimmick.character)
                }
            }
        }
    }
    return rosterCharacters;
}

async function checkCharacterUsage(character, roster, superstar) {  // check_character_usage
    const rosterCharacters = await getUsedCharacters(roster, superstar);
    return rosterCharacters.filter(x => x === character).length;
}

function getCharacterData(character, user, usedCharacters, unlockedCharacters) {  // get_character_data
    let data = {};

    data['name'] = character.name;
    data['level'] = character.level;
    data['tag'] = character.tag;

    // Check locked
    let locked;
    if (character.standard) {
        locked = false
    }
    else {
        locked = unlockedCharacters.indexOf(character.name) < 0;
    }
    data['locked'] = locked;

    // Add combinations and times used if not locked
    if (!locked) {
        data['combinations'] = {'easy': [], 'hard': [], 'okay': []};
        if (user) {
            let combinations = user.unlockedCombinations;
            if (combinations && combinations[character.name]) {
                for (const k of Object.keys(data['combinations'])) {
                    data['combinations'][k].push(...combinations[character.name][k])
                }
            }
        }

        data['timesUsed'] = usedCharacters.filter(x => x === character.name).length;

        // Get unlock counts
        data['easyCombinationsTotal'] = [...new Set(gimmickCombinationsData[character.name]['easy'])].length;
        data['hardCombinationsTotal'] = [...new Set(gimmickCombinationsData[character.name]['hard'])].length;
        data['easyCombinationsUnlocked'] = data['combinations']['easy'].length;
        data['hardCombinationsUnlocked'] = data['combinations']['hard'].length;
    }

    return data;
}

async function getRosterMemberAlignmentData({uid, type} = {}) {  // get_roster_member_alignment
    let data = {};

    // Get member
    let rosterMember;
    if (type === 'superstar' || type === 'training-superstar') {
        rosterMember = await Superstars.get(uid)
    }
    else if (type === 'tagTeam') {
        rosterMember = await TagTeams.get(uid)
    }

    data['name'] = rosterMember.name;
    if (type === 'tagTeam') {
        const tagTeamSuperstars = await getTagTeamSuperstars(rosterMember);
        if (tagTeamSuperstars.every(x => x.alignment === tagTeamSuperstars[0].alignment)) {
            data['alignment'] = tagTeamSuperstars[0].alignment
        }
        else {
            data['error'] = 'different-alignments';
            return data;
        }
    }
    else {
        data['alignment'] = rosterMember.alignment
    }

    return data;
}

async function getRosterMemberBrandData({uid, type} = {}) {  // get_roster_member_brand
    let data = {};

    // Get member
    let rosterMember;
    if (type === 'superstar' || type === 'training-superstar') {
        rosterMember = await Superstars.get(uid)
    }
    else if (type === 'tagTeam') {
        rosterMember = await TagTeams.get(uid)
    }

    data['name'] = rosterMember.name;
    if (type === 'tagTeam') {
        const tagTeamSuperstars = await getTagTeamSuperstars(rosterMember);
        if (tagTeamSuperstars.every(x => x.brandName === tagTeamSuperstars[0].brandName)) {
            data['brandName'] = tagTeamSuperstars[0].brandName
        }
    }
    else {
        data['brandName'] = rosterMember.brandName
    }

    // Get brand names
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterBrands = await getRosterBrands(mainRoster);
    data['brandNames'] = rosterBrands.map(x => x.name);

    return data;
}

async function changeRosterMemberName({uid, type, name} = {}) {  // change_roster_member_name
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const companyRosters = await getCompanyRosters(selectedCompany);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main', companyRosters);
    const trainingRoster = await getCompanyRosterByType(selectedCompany, 'training', companyRosters);
    const trainingAvailableRoster = await getCompanyRosterByType(selectedCompany, 'trainingAvailable', companyRosters);
    const cost = 50;

    // Get member
    let rosterMember;
    let allNames;
    if (type === 'superstar' || type === 'training-superstar') {
        rosterMember = await Superstars.get(uid);
        allNames = (await getRosterSuperstars(mainRoster)).concat(await getRosterSuperstars(trainingRoster),
            await getRosterSuperstars(trainingAvailableRoster)).map(x => x.name);
    }
    else if (type === 'tagTeam' || type === 'faction') {
        if (type === 'tagTeam') {
            rosterMember = await TagTeams.get(uid);
        }
        else {
            rosterMember = await Factions.get(uid)
        }
        allNames = (await getRosterTagTeams(mainRoster)).concat(await getRosterTagTeams(trainingRoster),
            await getRosterTagTeams(trainingAvailableRoster)).map(x => x.name);
        allNames.push(...((await getRosterActiveFactions(mainRoster)).concat(await getRosterActiveFactions(trainingRoster),
            await getRosterActiveFactions(trainingAvailableRoster)).map(x => x.name)));
    }

    // Check if enough points
    if (selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Check for same name
    if (rosterMember.name === name) {
        data['error'] = 'same-name';
        return data;
    }

    // Check for WoM
    if (selectedCompany.storyName === 'mars') {
        data['error'] = 'mars';
        return data;
    }

    // Check for PoW
    if (selectedCompany.storyName === 'passion') {
        const dialogPeopleNames = Object.keys(powDialog);
        if (dialogPeopleNames.indexOf(rosterMember.name) >= 0) {
            data['error'] = 'pow-person';
            return data;
        }
    }

    // Check for owner name change
    if (rosterMember.name === selectedCompany.ownerName) {
        data['error'] = 'owner-name-change';
        return data;
    }

    // Check for owner name
    if (name === selectedCompany.ownerName) {
        data['error'] = 'same-name-as-owner';
        return data;
    }

    // Check if name already exists on roster
    if (allNames.indexOf(name) >= 0) {
        data['error'] = 'name-exists';
        return data;
    }

    // Change name
    const previousName = rosterMember.name;
    if (name) {
        rosterMember.name = name
    }

    // Update company logs
    await updateCompanyLogsForTalentNameChange((await getCompanyCompanyLogs(selectedCompany)), rosterMember,
        previousName);

    // Remove points
    selectedCompany.points -= cost;

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;
    data['name'] = name;
    data['previousName'] = previousName;

    return data;
}

async function changeRosterMemberPronoun({uid, pronoun} = {}) {  // change_roster_member_pronoun
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);

    // Get roster member
    let rosterMember = await Superstars.get(uid);

    // Check for WoM
    if (selectedCompany.storyName === 'mars' || selectedCompany.storyName === 'passion') {
        data['error'] = selectedCompany.storyName;
        return data;
    }

    // Change gender and pronoun
    rosterMember.gender = getSuperstarGenderFromPronoun(pronoun);
    rosterMember.pronoun = getRosterMemberPronoun(rosterMember);
    rosterMember.possessivePronoun = getRosterMemberPronoun(rosterMember, 'possessive');
    rosterMember.objectivePronoun = getRosterMemberPronoun(rosterMember, 'objective');

    db.saveDatabase();

    // Get data to return
    data['pronoun'] = pronoun;

    return data;
}

async function changeRosterMemberAlignment({uid, type, alignment} = {}) {  // change_roster_member_alignment
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const cost = 30;

    // Check if enough points
    if (selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Get roster member
    let rosterMember;
    let rosterMemberSuperstars;
    if (type === 'superstar' || type === 'training-superstar') {
        rosterMember = await Superstars.get(uid);
        // Change alignment
        if (alignment !== rosterMember.alignment) {
            await turnRosterMember(rosterMember, alignment, selectedCompany)
        }
        else {
            data['error'] = 'same-alignment';
            return data;
        }
    }
    else if (type === 'tagTeam') {
        rosterMember = await TagTeams.get(uid);
        rosterMemberSuperstars = await getTagTeamSuperstars(rosterMember);
        if (alignment !== rosterMemberSuperstars[0].alignment) {
            for (const s of rosterMemberSuperstars) {
                await turnRosterMember(s, alignment, selectedCompany)
            }
        }
        else {
            data['error'] = 'same-alignment';
            return data;
        }
    }

     // Remove points
    selectedCompany.points -= cost;

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;
    data['alignment'] = alignment;
    data['alignmentMonths'] = 0;

    return data;
}

async function changeRosterMemberBrand({uid, type, brandName} = {}) {  // change_roster_member_brand_view
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterBrands = await getRosterBrands(mainRoster);
    const cost = 10;

    // Check if enough points
    if (selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Check if paid
    if (!globalUser.paid) {
        data['error'] = 'not-paid';
        return data;
    }

    // Check for story
    if (selectedCompany.storyName === 'mars' || selectedCompany.storyName === 'passion') {
        data['error'] = selectedCompany.storyName;
        return data;
    }

    // Get roster member
    let rosterMember;
    if (type === 'superstar') {
        rosterMember = await Superstars.get(uid);
    }
    else if (type === 'tagTeam') {
        rosterMember = await TagTeams.get(uid);
    }
    if (!rosterMember) {
        data['error'] = 'roster-member-not-found';
        return data;
    }

    // Check brand name
    if (brandName && rosterBrands.map(x => x.name).indexOf(brandName) < 0) {
        data['error'] = 'brand-name-not-found';
        return data;
    }

    // Change brand name
    let rosterMemberSuperstars = await getRosterMemberSuperstars(rosterMember);
    for (const superstar of rosterMemberSuperstars) {
        if (brandName) {
            setObjectBrand(superstar, brandName);
            // Check talent request
            const selectedBrand = rosterBrands.find(x => x.name === brandName);
            if (selectedBrand) {
                let requestData = {'brandId': selectedBrand.$loki};
                await checkTalentRequestAction({company: selectedCompany, requestType: 'change_brand', talent: superstar,
                    data: requestData});
            }
        }
        else {
            removeObjectBrand(superstar)
        }
    }

    // Remove points
    selectedCompany.points -= cost;

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;
    data['brandName'] = rosterMemberSuperstars[0].brandName;
    const unreadMessages = (await getCompanyMessages(selectedCompany)).filter(x => !x.read);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

async function changeSuperstarGraphic({uid, appearanceOption, graphicData} = {}) {  // change_graphic
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const cost = 15;

    // Get superstar
    let superstar = await Superstars.get(uid);
    const currentGraphic = superstar.graphic;
    const graphicPresent = currentGraphic !== null && Object.keys(currentGraphic).length !== 0;

    // Check if enough points
    if (graphicPresent && selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Change/remove graphic
    if (appearanceOption === 'change') {
        superstar.graphic = JSON.parse(graphicData)
    }
    else if (appearanceOption === 'remove') {
        superstar.graphic = null
    }

    // Check talent request
    if (!objectEquals(superstar.graphic, currentGraphic)) {
        await checkTalentRequestAction({company: selectedCompany, requestType: 'change_appearance', talent: superstar});
    }

    // Remove points
    if (graphicPresent) {
        selectedCompany.points -= cost;
    }

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;
    data['graphic'] = superstar.graphic ? JSON.parse(JSON.stringify(superstar.graphic)) : getEmptySuperstarGraphic(superstar);
    const unreadMessages = (await getCompanyMessages(selectedCompany)).filter(x => !x.read);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

async function addSuperstarGimmick({uid, character, personality, customGimmickName, appearanceOption, graphicData,
                                       superstarName, alignment} = {}) {  // create_gimmick_view
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const cost = 30;

    // Get superstar
    let superstar = await Superstars.get(uid);
    const superstarGimmick = await getSuperstarCurrentGimmick(superstar);
    const currentGraphic = superstar.graphic;

    // Check if enough points
    if (superstarGimmick && !superstarGimmick.nextGimmickBoost && selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Set gimmick name
    let gimmickName;
    if (customGimmickName) {
        gimmickName = customGimmickName
    }
    else {
        if (character && personality) {
            gimmickName = `${personality} ${character}`
        }
        else {
            gimmickName = personality || character
        }
    }

    // Check for same gimmick name
    if (superstarGimmick && superstarGimmick.name === gimmickName) {
        data['error'] = 'same-name';
        return data;
    }

    // Check usage of character
    let alreadyUsedPenalty = 0;
    if (character) {
        const timesUsed = await checkCharacterUsage(character, mainRoster, superstar);
        if (timesUsed > 0) {
            if (timesUsed >= 8) {
                alreadyUsedPenalty = -40
            }
            else if (timesUsed >= 5) {
                alreadyUsedPenalty = -15
            }
            else if (timesUsed >= 2) {
                alreadyUsedPenalty = -5
            }
        }
    }

    // Next gimmick bonus
    let nextGimmickBonus = 0;
    if (superstarGimmick && superstarGimmick.nextGimmickBoost) {
        nextGimmickBonus = 20
    }

    // Create gimmick
    const newGimmick = await createGimmick(superstar, gimmickName, character, alreadyUsedPenalty, nextGimmickBonus);
    superstar.debutNewGimmick = true;

    // Add combination bonus
    data['combinationResult'] = null;
    if (character && personality) {
        const combinationBonus = await getGimmickCreationBonus(character, personality);
        newGimmick.combinationBonus = combinationBonus;
        data['combinationResult'] = getGimmickCombinationResult(combinationBonus)
    }

    // Change/remove graphic
    if (appearanceOption === 'change') {
        superstar.graphic = JSON.parse(graphicData)
    }
    else if (appearanceOption === 'remove') {
        superstar.graphic = null
    }

    // Update name
    const previousName = superstar.name;
    if (superstarName && superstar.name !== superstarName && superstar.name !== selectedCompany.ownerName
        && selectedCompany.storyName !== 'mars') {

        // Check for POW
        if (selectedCompany.storyName === 'passion') {
            const dialogPeopleNames = Object.keys(powDialog);
            if (dialogPeopleNames.indexOf(superstar.name) >= 0) {
                data['error'] = 'pow-person';
                return data;
            }
        }

        superstar.name = superstarName;
        data['previousName'] = previousName;

        await updateCompanyLogsForTalentNameChange((await getCompanyCompanyLogs(selectedCompany)), superstar,
            previousName);
    }

    // Update alignment
    if (alignment !== superstar.alignment) {
        await turnRosterMember(superstar, alignment, selectedCompany);
        data['alignment'] = superstar.alignment;
        data['alignmentMonths'] = superstar.alignmentMonths;
    }

    // Check talent request
    if (!objectEquals(superstar.graphic, currentGraphic)) {
        await checkTalentRequestAction({company: selectedCompany, requestType: 'change_appearance', talent: superstar});
    }

    // Remove points
    if (superstarGimmick && !superstarGimmick.nextGimmickBoost) {
        selectedCompany.points -= cost;
    }

    db.saveDatabase();

    // Get data to return
    data['superstarName'] = superstar.name;
    data['superstarPossessivePronoun'] = superstar.possessivePronoun;
    data['points'] = selectedCompany.points;
    data['gimmick'] = getGimmickData(newGimmick, superstar);
    data['graphic'] = superstar.graphic ? JSON.parse(JSON.stringify(superstar.graphic)) : getEmptySuperstarGraphic(superstar);
    const unreadMessages = (await getCompanyMessages(selectedCompany)).filter(x => !x.read);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

async function createGimmick(superstar, gimmickName, character, alreadyUsedPenalty = 0, nextGimmickBonus = 0,
                             characterBonus = 0) {  // create_gimmick
    const gimmick = Gimmick();
    gimmick.name = gimmickName;

    // Get character's bonus or add bonus directly
    if (character) {
        const characterData = charactersData.find(x => x.name === character);
        const bonuses = {1: 0, 2: 10, 3: 30};
        gimmick.character = character;
        gimmick.characterBonus = bonuses[characterData.level] || 0;
    }
    else {
        gimmick.characterBonus = characterBonus
    }

    gimmick.characterBonus += alreadyUsedPenalty + nextGimmickBonus;

    // Add to superstar
    await Gimmicks.insert(gimmick);
    await addSuperstarGimmicks(superstar, [gimmick]);

    return gimmick;
}

async function getGimmickCreationBonus(character, personality) {  // get_gimmick_creation_bonus
    let bonus = 0;
    let bonusName;
    let data = gimmickCombinationsData[character];
    if (data) {
        if (data['easy'].indexOf(personality) >= 0) {
            bonus = 10;
            bonusName = 'easy';
        }
        else if (data['hard'].indexOf(personality) >= 0) {
            bonus = -10;
            bonusName = 'hard';
        }
        else {
            bonus = 0;
            bonusName = 'okay';
        }
        // Add combination to user
        const user = loadUser();
        if (user && bonusName) {
            if (!user.unlockedCombinations || Object.keys(user.unlockedCombinations).length === 0) {
                await addEmptyCombinationsToUser(user)
            }
            await addCombinationToUser(user, character, personality, bonusName);
            saveUser(user);
        }
    }
    return bonus
}

async function addEmptyCombinationsToUser(user) {  // add_empty_combinations_to_user
    let data = {};
    for (const c of charactersData) {
        data[c.name] = {
            'easy': [],
            'hard': [],
            'okay': []
        }
    }
    user.unlockedCombinations = data;
    return data;
}

async function addCombinationToUser(user, character, personality, value) {  // add_combination_to_user
    if (!user.unlockedCombinations.hasOwnProperty(character) && charactersData.find(x => x.name === character)) {  // Adds new character
        user.unlockedCombinations[character] = {
            'easy': [],
            'hard': [],
            'okay': []
        }
    }
    if (user.unlockedCombinations[character] && user.unlockedCombinations[character][value]) {
        if (user.unlockedCombinations[character][value].indexOf(personality) < 0) {
            user.unlockedCombinations[character][value].push(personality);
        }
    }
}

async function removeSuperstarGimmick({uid} = {}) {  // remove_gimmick_view
    let data = {};

    // Get superstar
    let superstar = await Superstars.get(uid);
    if (!superstar) {
        data['error'] = 'superstar-not-found';
        return data;
    }

    // Get gimmick
    const gimmick = await getSuperstarCurrentGimmick(superstar);
    if (!gimmick) {
        data['error'] = 'no-gimmick-present';
        return data;
    }

    // Remove gimmick
    await removeSuperstarGimmicks(superstar, [gimmick]);
    superstar.debutNewGimmick = false;

    db.saveDatabase();

    return data;
}

async function turnRosterMember(rosterMember, alignment, company) {  // turn_participant
    for (const superstar of (await getRosterMemberSuperstars(rosterMember))) {
        superstar.alignment = alignment;
        superstar.alignmentMonths = 0;
        // Check talent request
        let requestData = {'alignment': alignment};
        await checkTalentRequestAction({company: company, requestType: 'change_alignment', talent: superstar, data: requestData});
    }
}

async function addSuperstar(name, age, pronoun, alignment, partTimer, charisma, workrate, fullTimeManager,
        brandName, graphicData, addGraphic, selectedTraits) {  // add_custom_superstar
    let data = {};
    const currentGame = await getCurrentGame();
    let selectedCompany = await getGameCurrentUserCompany(currentGame);
    const companyRosters = await getCompanyRosters(selectedCompany);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main', companyRosters);
    const trainingRoster = await getCompanyRosterByType(selectedCompany, 'training', companyRosters);
    const trainingAvailableRoster = await getCompanyRosterByType(selectedCompany, 'trainingAvailable', companyRosters);
    const trainingFutureRoster = await getCompanyRosterByType(selectedCompany, 'trainingFuture', companyRosters);
    let rosterSuperstars = await getRosterSuperstars(mainRoster);
    let trainingSuperstars = (await getRosterSuperstars(trainingRoster))
        .concat(await getRosterSuperstars(trainingAvailableRoster));
    let futureSuperstars = trainingFutureRoster ? await getRosterSuperstars(trainingFutureRoster) : [];
    const rosterBrands = await getRosterBrands(mainRoster);

    // Get selected traits
    let traits = [];
    if (selectedTraits.length > 0) {
        traits = selectedTraits.filter(x => Object.keys(traitsData).indexOf(x) >= 0)
    }

    // Get cost
    let cost = 100;
    cost += (workrate - 1) * 75;
    cost += (charisma - 1) * 75;
    cost += traits.length * 25;

    // Check if enough points
    if (selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Get gender
    const gender = getSuperstarGenderFromPronoun(pronoun);

    // Check age
    if (age > 100 || age < 10) {
        data['error'] = 'age';
        return data;
    }

    // Check manager
    if (fullTimeManager && !globalUser.paid) {
        data['error'] = 'managers-paid';
        return data;
    }

    // Check brand
    if (brandName && !globalUser.paid) {
        data['error'] = 'brands-paid';
        return data;
    }

    // Check if superstar already exists (roster, training, future training)
    const mainRosterNames = rosterSuperstars.map(x => x.name);
    const trainingRosterNames = trainingSuperstars.map(x => x.name);
    const futureRosterNames = futureSuperstars.map(x => x.name);
    if (mainRosterNames.concat(trainingRosterNames, futureRosterNames).indexOf(name) >= 0) {
        data['error'] = 'already-exists';
        if (mainRosterNames.indexOf(name) >= 0) {
            data['existsRoster'] = 'Main'
        }
        if (trainingRosterNames.indexOf(name) >= 0) {
            data['existsRoster'] = 'Training'
        }
        if (futureRosterNames.indexOf(name) >= 0) {
            data['existsRoster'] = 'future'
        }
        return data;
    }

    // Check if name is same as owner name
    if (name === selectedCompany.ownerName) {
        data['error'] = 'owner-name';
        return data;
    }

    // Check brand name
    if (brandName && rosterBrands.map(x => x.name).indexOf(brandName) < 0) {
        data['error'] = 'brand-not-found';
        return data;
    }

    // Add superstar
    if (!addGraphic) {
        graphicData = null
    }
    let superstar = await createCustomSuperstar(name, age, gender, alignment, workrate, charisma, partTimer, traits,
        fullTimeManager, graphicData);

    // Set brand name
    if (brandName) {
        setObjectBrand(superstar, brandName)
    }

    // Add to roster
    await addRosterSuperstars(mainRoster, [superstar]);

    // Add company log
    const newCompanyLog = CompanyLog(currentGame.monthCount, selectedCompany.realYear);
    await CompanyLogs.insert(newCompanyLog);
    newCompanyLog.text = superstar.name;
    newCompanyLog.type = 'new-superstar';
    newCompanyLog.realYear = currentGame.year;
    await addCompanyCompanyLogs(selectedCompany, [newCompanyLog]);

    // Remove points
    selectedCompany.points -= cost;

    // Add to community graphics if online
    if (graphicData && window.navigator.onLine) {
        $.ajax({
            url: globals.baseUrl + '/_add_template_superstar',
            dataType: 'json',
            data: {name: name, age: age, graphic: graphicData},
            success: async (data) => {
                if (!data['error']) {
                    notie.alert({
                        type: 4,
                        text: '<span class="display-block">{0}\'s graphic has been added to Community Graphics</span>'.format(name)
                    });
                }
            }
        });
    }

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;
    data['superstarName'] = superstar.name;

    return data;
}

async function releaseTalent({uid, type} = {}) {  // release_superstar
    let data = {};
    const currentGame = await getCurrentGame();
    let selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainCompany = await getGameMainUserCompany(currentGame);
    const indieRoster = await getCompanyRosterByType(mainCompany, 'indie');
    const indieAvailableRoster = await getCompanyRosterByType(mainCompany, 'indieAvailable');
    const companyChampionships = await getCompanyChampionships(selectedCompany);
    const latestEvent = await getCompanyLatestEvent(selectedCompany);

    // Get talent
    let talent;
    let talentSuperstars;
    if (type === 'tagTeam') {
        talent = await TagTeams.get(uid);
        talentSuperstars = await getTagTeamSuperstars(talent)
    }
    else {
        talent = await Superstars.get(uid);
        talentSuperstars = [talent];
    }
    if (!talent) {
        data['error'] = 'not-found';
        return data;
    }

    // Don't allow champions
    for (const s of talentSuperstars) {
        const superstarChampionships = await getSuperstarChampionships(s);
        const freebirdChampionships = await getSuperstarFreebirdTagChampionships(s, companyChampionships);
        const stolenChampionshipRandomEvent = (await getSuperstarRandomEvents(s))
            .find(x => x.name === 'championshipStolen' && x.monthsRemaining > 0);
        if (superstarChampionships.length > 0 || freebirdChampionships.length > 0 || stolenChampionshipRandomEvent) {
            data['error'] = 'champion';
            return data;
        }
    }

    // Don't allow on trade
    if (talentSuperstars.some(x => x.activeEffect === 'traded')) {
        data['error'] = 'trade-deal';
        return data;
    }

    // Don't allow booked
    const bookedSuperstars = await getSuperstarsBookedAtEventMatches(latestEvent);
    if (talentSuperstars.some(x => objectInArray(x, bookedSuperstars))) {
        data['error'] = 'booked';
        return data;
    }

    // Don't allow martians
    if (selectedCompany.storyName === 'mars') {
        if (talentSuperstars.some(x => x.name.indexOf('(Martian)') >= 0)) {
            data['error'] = 'martian';
            return data;
        }
    }

    // Don't allow POW stars
    if (selectedCompany.storyName === 'passion') {
        if (talentSuperstars.some(x =>
            ['Lady Julia', 'Violet', 'Natasha Ali', 'Zora'].indexOf(x.name) >= 0)) {
            data['error'] = 'pow-person';
            return data;
        }
    }

    // Remove superstar(s) from their roster and add to indie roster
    const overnessDecrease = getRandomInt(8, 12);
    for (const s of talentSuperstars) {
        if (currentGame.settings['all_indies_available']) {
            await addRosterSuperstars(indieAvailableRoster, [s])
        }
        else {
            await addRosterSuperstars(indieRoster, [s])
        }

        // End feuds
        for (const feud of (await getSuperstarFeuds(s))) {
            if (!feud.ended) {
                 await endFeud(feud, currentGame.monthCount, currentGame.year);
            }
        }

        // Remove teams/managers/factions
        await removeSuperstarTagTeams(s);
        await removeSuperstarFactionRelationships(s, currentGame.monthCount, currentGame.year);
        await removeSuperstarManagerRelationships(s);

        // Delete all relationships, add former company relationship
        for (const relationship of (await getSuperstarRelationships(s))) {
            await Relationships.remove(relationship)
        }
        await addFormerCompanyRelationship(s, selectedCompany);

        // Delete talent requests
        for (const talentRequest of (await getSuperstarTalentRequests(s))) {
            await TalentRequests.remove(talentRequest)
        }

        // Make superstar happy
        changeSuperstarMoraleToHappy(s);

        // Decrease overness
        s.overness -= overnessDecrease;

        // Reset attributes
        s.brandName = null;
        s.faceOfTheCompanyCounter = 0;
        s.suspensionMonths = 0;

        // Add company log
        const newCompanyLog = CompanyLog(currentGame.monthCount, selectedCompany.realYear);
        await CompanyLogs.insert(newCompanyLog);
        newCompanyLog.text = s.name;
        newCompanyLog.type = 'release';
        newCompanyLog.realYear = currentGame.year;
        await addCompanyCompanyLogs(selectedCompany, [newCompanyLog]);
    }

    let newsItem = await generateNewsItem({company: selectedCompany, month: currentGame.monthCount, year: currentGame.year,
        superstar: talent, newsType: 'superstar_released_by_company'});
    await addCompanyNewsItems(mainCompany, [newsItem]);

    db.saveDatabase();

    // Get data to return
    data['name'] = talent.name;
    data['possessivePronoun'] = talent.possessivePronoun;
    data['overnessDecrease'] = overnessDecrease;

    return data;
}

async function createCustomSuperstar(name, age, gender, alignment, workrate, charisma, partTimer, traits,
        fullTimeManager, graphicData) {  // create_custom_superstar
    const xps = {3: 300, 2: 100, 1: 0};

    const superstar = Superstar();
    superstar.name = name;
    superstar.age = age;
    superstar.gender = gender;
    superstar.pronoun = getRosterMemberPronoun(superstar);
    superstar.possessivePronoun = getRosterMemberPronoun(superstar, 'possessive');
    superstar.objectivePronoun = getRosterMemberPronoun(superstar, 'objective');
    superstar.popularity = getRandomInt(500, 600);
    superstar.graphic = JSON.parse(graphicData);
    superstar.partTimer = partTimer;
    if (partTimer) {
        superstar.appearances = getPartTimerAppearances(superstar)
    }
    superstar.alignment = alignment;
    superstar.charisma = charisma;
    superstar.highestRank = getRankFromPopularity(superstar.popularity);
    superstar.charismaXp = xps[charisma] || 0;
    superstar.workrate = workrate;
    superstar.workrateXp = xps[workrate] || 0;
    superstar.fullTimeManager = fullTimeManager;
    if (traits.length > 0) {
        superstar.traits = traits
    }

    await Superstars.insert(superstar);

    return superstar;
}

function getPartTimerAppearances(superstar) {  // get_part_timer_appearances
    if (superstar.partTimer) {
        return getRandomInt(1, 8)
    }
    return null
}

async function disbandTagTeam({uid} = {}) {  // disband_tag_team
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const nextEvent = await getCompanyLatestEvent(selectedCompany);

    // Get tag team
    const tagTeam = await TagTeams.get(uid);
    if (!tagTeam) {
        data['error'] = 'not-found';
        return data;
    }

    const tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);
    const trio = tagTeam.trio;

    // Don't allow champions
    let tagChampionships = (await Promise.all(tagTeamSuperstars.map(x => getSuperstarChampionships(x)))).flat();
    if (trio) {
        tagChampionships = tagChampionships.filter(x => x.trio || x.tagTeam)
    }
    else {
        tagChampionships = tagChampionships.filter(x => x.tagTeam)
    }
    if (tagChampionships.length > 0) {
        data['error'] = 'champion';
        return data;
    }

    // Don't allow booked
    const bookedSuperstars = await getSuperstarsBookedAtEventMatches(nextEvent);
    if (tagTeamSuperstars.some(x => objectInArray(x, bookedSuperstars))) {
        data['error'] = 'booked';
        return data;
    }
    const eventSegments = await getEventSegments(nextEvent);
    const eventSegmentsParticipants = (await Promise.all(eventSegments.map(x => getSegmentParticipants(x)))).flat();
    if (objectInArray(tagTeam, eventSegmentsParticipants)) {
        data['error'] = 'booked';
        return data;
    }

    // Remove tag team
    await removeTagTeam(tagTeam);

    // Add feud potential
    if (tagTeamSuperstars.length > 1) {
        const feudItem = (await getCompanyFeudItems(selectedCompany))
            .find(x => x.unlocked && x.requirement === 'disbanded-tag-team');
        if (feudItem) {
            const feudSuperstars = getRandomSample(tagTeamSuperstars, 2);
            const newFeud = Feud();
            newFeud.name = feudItem.name;
            newFeud.hype = feudItem.baseHype;
            newFeud.participants = feudSuperstars.map(x => x.name);
            newFeud.directFeudEvent = 'disbanded-tag-team';
            newFeud.startMonth = currentGame.monthCount;
            newFeud.startYear = currentGame.year;
            await Feuds.insert(newFeud);
            await addFeudItemFeuds(feudItem, [newFeud]);
            await addRosterFeuds(mainRoster, [newFeud]);
            await addFeudSuperstars(newFeud, feudSuperstars);
            data['feudPotential'] = {'name': newFeud.name, 'participants': newFeud.participants};
        }
    }

    // Check talent request
    for (const s of tagTeamSuperstars) {
        let requestData = {'teamId': tagTeam.$loki};
        await checkTalentRequestAction({company: selectedCompany, requestType: 'disband_team', talent: s, data: requestData});
    }

    // Check tutorial action
    if (selectedCompany.storyName === 'tutorial') {
        data['nextTutorialState'] = await checkToGoToNextTutorialState(selectedCompany, ['disband_team']);
    }

    db.saveDatabase();

    // Get data to return
    data['name'] = tagTeam.name;
    const unreadMessages = (await getCompanyMessages(selectedCompany)).filter(x => !x.read);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

async function getBookingData({matchType, tagTeamType, segment = false,
                               getVacatedChampionships = true} = {}) {  // get_roster_view / get_roster
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    let rosterSuperstars = await getRosterSuperstars(mainRoster);
    const companyChampionships = await getCompanyChampionships(selectedCompany);
    const nextEvent = await getCompanyLatestEvent(selectedCompany);
    const rosterBrands = await getRosterBrands(mainRoster);
    const staffMembers = (await getCompanyStaff(selectedCompany)).filter(x => !x.retired);
    const doctor = getHighestLevelStaffMemberByRole(staffMembers, 'doctor');
    let vacatedChampionships = [];

    // Define data
    data['available'] = [];
    data['notAvailable'] = [];
    data['retired'] = [];
    data['managers'] = [];
    data['teams'] = [];
    data['vacatedChampionships'] = [];
    data['factions'] = [];
    data['brandNames'] = rosterBrands.map(x => x.name);

    if (matchType === 'singles') {
        const rosterTagTeams = await getRosterActiveTagTeams(mainRoster);
        const rosterFactions = await getRosterActiveFactions(mainRoster);
        const competitors = await getActiveCompetitorsForCompany(selectedCompany, currentGame);
        const competitorChampionships = (await Promise.all(competitors.map(x => getCompanyChampionships(x)))).flat();
        const allChampionships = companyChampionships.concat(competitorChampionships);
        const bookedSuperstars = await getSuperstarsBookedAtEventMatches(nextEvent);

        // Get available superstars
        let availableSuperstars = rosterSuperstars.filter(x => !x.dead);

        // Allow retired superstars and managers in segments
        if (!segment) {
            availableSuperstars = availableSuperstars.filter(x => !(x.retired && !x.fullTimeManager) &&
                (!x.fullTimeManager || x.activeEffect === 'one_match_appearance'));
        }

        // Sort superstars by popularity and name
        availableSuperstars.sort((a, b) => b.popularity - a.popularity || b.name.localeCompare(a.name));

        // Add temporary participants to tag teams and factions
        for (const x of rosterTagTeams.concat(rosterFactions)) {
            x.tempSuperstarsIds = (await getRosterMemberSuperstars(x)).map(x => x.$loki)
        }

        // Get data for superstars
        for (const superstar of availableSuperstars) {

            // Get superstar tagTeams, factions and championships from already loaded data
            let superstarTagTeams = rosterTagTeams.filter(x => x.tempSuperstarsIds.includes(superstar.$loki));
            let superstarFactions = rosterFactions.filter(x => x.tempSuperstarsIds.includes(superstar.$loki));
            let superstarTagTeamsAndFactions = superstarTagTeams.concat(superstarFactions);
            let superstarChampionships = allChampionships.filter(x => x.superstarId === superstar.$loki);
            superstarChampionships.push(...await getSuperstarFreebirdTagChampionships(superstar, allChampionships,
                superstarTagTeams));

            // Check for brand exclusive event
            if (!checkSuperstarBrandAvailability(nextEvent, superstar, superstarChampionships)) {
                continue
            }

            // Get data for superstar
            const superstarData = await getSuperstarDisplayData({superstar: superstar, doctor: doctor,
                getTeamsAndFactions: true, rosterSuperstars: rosterSuperstars, getGimmick: false,
                superstarChampionships: superstarChampionships, game: currentGame,
                superstarTagTeamsAndFactions: superstarTagTeamsAndFactions});

            // Add booked
            if (!segment && objectInArray(superstar, bookedSuperstars)) {
                superstarData['booked'] = true
            }

            // Add to managers
            if (superstar.fullTimeManager) {
                data['managers'].push(superstarData);
                continue;
            }

            // Add to retired
            if (superstar.retired) {
                data['retired'].push(superstarData);
                continue;
            }

            // Add to not available if part timer with 0 appearances
            if (superstar.partTimer && superstar.appearances <= 0) {
                data['notAvailable'].push(superstarData);
                continue;
            }

            // Add to not available if injured (except workable injury)
            if (superstar.injured > 0 && !superstar.workableInjury) {
                data['notAvailable'].push(superstarData);
                continue;
            }

            // Add to not available if suspended or unavailable
            if (superstar.suspensionMonths > 0 || superstar.unavailable) {
                data['notAvailable'].push(superstarData);
                continue;
            }

            // Add to available
            data['available'].push(superstarData);
        }

        // Get vacated championships
        vacatedChampionships = companyChampionships.filter(x => x.vacated && !x.booked);
        if (tagTeamType) {
            // Get team titles if singles tag match (2v2, 3v3)
            if (tagTeamType === 'tag-team') {
                vacatedChampionships = vacatedChampionships.filter(x => x.tagTeam)
            }
            else if (tagTeamType === 'trio') {
                vacatedChampionships = vacatedChampionships.filter(x => x.trio)
            }
        }
        else {
            // Get singles championships
            vacatedChampionships = vacatedChampionships.filter(x => !x.tagTeam && !x.trio);
        }
    }
    else if (matchType === 'tag') {
        const rosterTagTeams = (await getRosterActiveTagTeams(mainRoster))
            .filter(x => (tagTeamType === 'trio') ? x.trio : true);
        const competitors = await getActiveCompetitorsForCompany(selectedCompany, currentGame);
        const competitorChampionships = (await Promise.all(competitors.map(x => getCompanyChampionships(x)))).flat();
        const allChampionships = companyChampionships.concat(competitorChampionships)
            .filter(x => (tagTeamType === 'trio') ? x.trio : x.tagTeam);

        // Get data for tag teams
        for (const tagTeam of rosterTagTeams) {

            let tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);

            // Remove tag team if no superstars present
            if (tagTeamSuperstars.length <= 0) {
                await removeTagTeam(tagTeam);
                db.saveDatabase();
                continue;
            }

            // Check if temporary team
            if (tagTeam.temporary) {
                continue
            }

            // Get championships
            let tagTeamChampionships = allChampionships
                .filter(x => tagTeamSuperstars.some(y => x.superstarId === y.$loki));

            // Allow one unavailable superstar for trios in tag match booking (not in segment)
            let maxUnavailableMembers = 0;
            if (tagTeamType === 'tag-team' && tagTeam.trio && !segment) {
                maxUnavailableMembers = 1
            }

            // Check for excluded superstars
            const excludedSuperstars = tagTeamSuperstars
                .filter(x => !checkSuperstarBrandAvailability(nextEvent, x,
                    getSuperstarChampionshipsFromCompanyChampionships(x, companyChampionships)));
            if (excludedSuperstars.length > maxUnavailableMembers) {
                continue
            }

            // Filter out excluded superstars
            tagTeamSuperstars = tagTeamSuperstars.filter(x => !objectInArray(x, excludedSuperstars));

            // Get data for tagTeam
            const tagTeamData = await getTagTeamDisplayData({tagTeam: tagTeam, rosterSuperstars: rosterSuperstars,
                tagTeamChampionships: tagTeamChampionships, game: currentGame});

            // Get not available superstars
            let notAvailableSuperstars;
            if (segment) {
                notAvailableSuperstars = tagTeamSuperstars.filter(x => !checkSuperstarSegmentAvailability(x))
            }
            else {
                notAvailableSuperstars = tagTeamSuperstars.filter(x => !checkAvailability(x, false))
            }

            // Add to not available or teams
            if (notAvailableSuperstars.length > maxUnavailableMembers) {
                data['notAvailable'].push(tagTeamData)
            }
            else {
                data['teams'].push(tagTeamData)
            }
        }

        // Sort teams
        data['teams'].sort((a, b) => b.popularity - a.popularity || b.name.localeCompare(a.name));

        // Get vacated championships
        vacatedChampionships = companyChampionships
            .filter(x => x.vacated && !x.booked && ((tagTeamType === 'trio') ? x.trio : x.tagTeam));
    }
    if (matchType === 'factions' || (matchType === 'tag' && !segment)) {
        const rosterFactions = await getRosterActiveFactions(mainRoster);

        // Get data for factions
        for (const faction of rosterFactions) {
            let factionSuperstars = await getFactionSuperstars(faction);

            let minAvailableMembers = (tagTeamType === 'trio') ? 3 : 2;
            if (segment) {
                minAvailableMembers = 2
            }

            // Check for excluded superstars
            factionSuperstars = factionSuperstars.filter(x => checkSuperstarBrandAvailability(nextEvent, x,
                getSuperstarChampionshipsFromCompanyChampionships(x, companyChampionships)));
            if (factionSuperstars.length < minAvailableMembers) {
                continue
            }

            // Get data for faction
            const factionData = await getFactionData(faction, companyChampionships, true);

            // Get not available superstars
            let availableSuperstars;
            if (segment) {
                availableSuperstars = factionSuperstars.filter(x => checkSuperstarSegmentAvailability(x))
            }
            else {
                availableSuperstars = factionSuperstars.filter(x => checkAvailability(x, false))
            }

            // Add to not available or teams
            if (availableSuperstars.length < minAvailableMembers) {
                data['notAvailable'].push(factionData)
            }
            else {
                data['factions'].push(factionData)
            }
        }
    }

    // Filter vacated championships
    vacatedChampionships = vacatedChampionships.filter(x => checkObjectBrandAvailability(x, nextEvent.brandName, false,
        nextEvent.activeEffect));
    vacatedChampionships = getUniqueChampionships(vacatedChampionships);

    // Get data for vacated championships
    if (getVacatedChampionships) {
        data['vacatedChampionships'] = await Promise.all(vacatedChampionships.map(x => getChampionshipBookingData(x)));
    }

    return data;
}

async function getBookingSelectionInformation({uid, participantType} = {}) {  // get_booking_selection_information
    let data = {};
    const currentGame = await getCurrentGame();

    // Get participant and feuds
    let feuds = [];
    if (participantType === 'superstar') {
        const superstar = await Superstars.get(uid);
        if (!superstar) {
            return data;
        }
        feuds = await getSuperstarFeuds(superstar);

        // Get faction and team member names
        let factions = await getSuperstarActiveFactions(superstar);
        let tagTeams = (await getSuperstarTagTeams(superstar)).filter(x => !x.temporary);
        data['partnerSuperstars'] = (await Promise.all(factions.map(x => getFactionSuperstars(x)))).flat()
            .filter(x => x.$loki !== superstar.$loki).map(x => x.name);
        data['partnerSuperstars'].push(...((await Promise.all(tagTeams.map(x => getTagTeamSuperstars(x)))).flat()
            .filter(x => x.$loki !== superstar.$loki).map(x => x.name)));
    }
    else if (participantType === 'tag-team' || participantType === 'trio') {
        const tagTeam = await TagTeams.get(uid);
        if (!tagTeam) {
            return data;
        }
        feuds = await getTagTeamFeuds(tagTeam);
    }
    else if (participantType === 'faction') {
        const faction = await Factions.get(uid);
        if (!faction) {
            return data;
        }
        feuds = await getFactionFeuds(faction);
    }

    // Sort feuds by not ended, then end time
    feuds.sort((a, b) => a.ended - b.ended || b.endYear - a.endYear || b.endMonth - a.endMonth);

    // Get data for feuds
    data['feudsData'] = [];
    for (const feud of feuds) {
        // Skip ended potential feuds
        if (feud.ended && !feud.started) {
            continue;
        }

        let feudData = await getFeudData(feud, true);

        // Skips previous feud if current feud present and skips feuds before latest previous feud
        if (data['feudsData'].some(x => checkAreArraysEqual(x['participants'], feudData['participants']))) {
            continue
        }

        // Add previous feud boost
        if (feud.started && feud.ended) {
            feudData['previousFeudBoost'] = getPreviousFeudHypeBoost(feud, currentGame.monthCount, currentGame.year);
        }

        data['feudsData'].push(feudData);
    }

    return data;
}

export {getAvailableCallupsData, callupAvailableTrainingTalent, getViewRosterData, getViewRosterMemberData,
    getViewIndiesData, getViewTeamsData, getViewManagersData, getRosterActiveTagTeams, getAddTeamData, addTeam,
    getRosterMemberNameData, addSuperstar, releaseTalent, changeRosterMemberName, getRosterMemberPronounData,
    getRosterMemberAlignmentData, getRosterMemberBrandData, getRosterMemberGimmickData, changeRosterMemberPronoun,
    changeRosterMemberAlignment, changeRosterMemberBrand, changeSuperstarGraphic, removeSuperstarGimmick,
    addSuperstarGimmick, disbandTagTeam, transferTalentToRoster, getPartTimerAppearances, getBookingData,
    getBookingSelectionInformation, callupTalentToMainRoster, turnRosterMember, checkCharacterUsage, createGimmick,
    getGimmickCreationBonus}